import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import Payments from "./pages/Payments";
import TurmasFixas from "./pages/TurmasFixas";
import Courts from "./pages/Courts";
import Events from "./pages/Events";
import Store from "./pages/Store";
import StudentPortal from "./pages/StudentPortal";
import TrialClass from "./pages/TrialClass";
import TrialClassAdmin from "./pages/TrialClassAdmin";
import Notifications from "./pages/Notifications";
import Subscriptions from "./pages/Subscriptions";
import LeaguePublic from "./pages/LeaguePublic";
import DropInClass from "./pages/DropInClass";
import DropInFem from "./pages/DropInFem";
import JogoTreinoPublic from "./pages/JogoTreinoPublic";
import OpenPlayPublic from "./pages/OpenPlayPublic";
import DropInAdmin from "./pages/DropInAdmin";
import OpenPlayAdmin from "./pages/OpenPlayAdmin";
import JogoTreinoAdmin from "./pages/JogoTreinoAdmin";
import FuncionalAdmin from "./pages/FuncionalAdmin";
import CourtRentalAdmin from "./pages/CourtRentalAdmin";
import CourtRentalPublic from "./pages/CourtRentalPublic";
import StudentDetail from "./pages/StudentDetail";
import Birthdays from "./pages/Birthdays";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AllPayments from "./pages/AllPayments";
import Settings from "./pages/Settings";
import StorePublic from "./pages/StorePublic";
import RankingsAdmin from "./pages/RankingsAdmin";
import TournamentAdmin from "./pages/TournamentAdmin";
import TournamentPublic from "./pages/TournamentPublic";
import LiveScore from "./pages/LiveScore";
import TurmasManage from "./pages/TurmasManage";
import Reports from "./pages/Reports";
import SubscriptionsBilling from "./pages/SubscriptionsBilling";
import ChargesAdmin from "./pages/ChargesAdmin";
import ChargePay from "./pages/ChargePay";
import EventsAdmin from "./pages/EventsAdmin";
import EventPay from "./pages/EventPay";
import OrderBalancePay from "./pages/OrderBalancePay";

// Smart redirect based on role
function SmartRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/dashboard" replace />;
  return <Navigate to="/portal" replace />;
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <Toaster 
        position="top-right" 
        richColors 
        toastOptions={{
          style: {
            fontFamily: "'DM Sans', sans-serif"
          }
        }}
      />
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/tk-league" element={<LeaguePublic />} />
          <Route path="/aula-avulsa" element={<DropInClass />} />
          <Route path="/avulso-feminino" element={<DropInFem />} />
          <Route path="/jogo-treino" element={<JogoTreinoPublic />} />
          <Route path="/open-play" element={<OpenPlayPublic />} />
          <Route path="/cobranca/:id" element={<ChargePay />} />
          <Route path="/evento/:id" element={<EventPay />} />
          <Route path="/funcional" element={<Navigate to="/open-play" replace />} />
          <Route path="/loja" element={<StorePublic />} />
          <Route path="/pedido/:collection/:orderId" element={<OrderBalancePay />} />
          <Route path="/tk-league/torneio-publico" element={<TournamentPublic />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          
          {/* Student Portal */}
          <Route path="/portal" element={
            <ProtectedRoute>
              <StudentPortal />
            </ProtectedRoute>
          } />
          
          {/* Admin protected routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/students" element={
            <ProtectedRoute>
              <Students />
            </ProtectedRoute>
          } />
          <Route path="/payments" element={
            <ProtectedRoute>
              <Payments />
            </ProtectedRoute>
          } />
          <Route path="/classes" element={
            <ProtectedRoute>
              <TurmasFixas />
            </ProtectedRoute>
          } />
          <Route path="/courts" element={
            <ProtectedRoute>
              <Courts />
            </ProtectedRoute>
          } />
          <Route path="/events" element={
            <ProtectedRoute>
              <Events />
            </ProtectedRoute>
          } />
          <Route path="/store" element={
            <ProtectedRoute>
              <Store />
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } />
          <Route path="/subscriptions" element={
            <ProtectedRoute>
              <Subscriptions />
            </ProtectedRoute>
          } />
          <Route path="/subscriptions-billing" element={
            <ProtectedRoute>
              <SubscriptionsBilling />
            </ProtectedRoute>
          } />
          <Route path="/cobrancas" element={
            <ProtectedRoute>
              <ChargesAdmin />
            </ProtectedRoute>
          } />
          <Route path="/eventos" element={
            <ProtectedRoute>
              <EventsAdmin />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/drop-in-admin" element={
            <ProtectedRoute>
              <DropInAdmin />
            </ProtectedRoute>
          } />
          <Route path="/open-play-admin" element={
            <ProtectedRoute>
              <OpenPlayAdmin />
            </ProtectedRoute>
          } />
          <Route path="/jogo-treino-admin" element={
            <ProtectedRoute>
              <JogoTreinoAdmin />
            </ProtectedRoute>
          } />
          <Route path="/funcional-admin" element={
            <ProtectedRoute>
              <FuncionalAdmin />
            </ProtectedRoute>
          } />
          <Route path="/court-rental-admin" element={
            <ProtectedRoute>
              <CourtRentalAdmin />
            </ProtectedRoute>
          } />
          <Route path="/aluguel-quadra" element={<CourtRentalPublic />} />
          <Route path="/students/:id" element={
            <ProtectedRoute>
              <StudentDetail />
            </ProtectedRoute>
          } />
          <Route path="/birthdays" element={
            <ProtectedRoute>
              <Birthdays />
            </ProtectedRoute>
          } />
          <Route path="/all-payments" element={
            <ProtectedRoute>
              <AllPayments />
            </ProtectedRoute>
          } />
          <Route path="/rankings" element={
            <ProtectedRoute>
              <RankingsAdmin />
            </ProtectedRoute>
          } />
          <Route path="/tk-league/torneio" element={
            <ProtectedRoute>
              <TournamentAdmin />
            </ProtectedRoute>
          } />
          <Route path="/tk-league/placar" element={
            <ProtectedRoute>
              <LiveScore />
            </ProtectedRoute>
          } />
          <Route path="/turmas-horarios" element={
            <ProtectedRoute>
              <TurmasManage />
            </ProtectedRoute>
          } />
          <Route path="/relatorios" element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          } />
          
          {/* Smart redirect based on role */}
          <Route path="/" element={<SmartRedirect />} />
          
          {/* 404 - Smart redirect */}
          <Route path="*" element={<SmartRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
